/**
 * PortalErrorBoundary — safety net for authenticated portal routes.
 *
 * Catches uncaught render errors + surfaces a graceful "Something went
 * wrong" screen so the user never sees a raw stack trace or (via
 * direct-navigation API leaks) raw `{"detail":"..."}` JSON.
 *
 * This is a class component because React error boundaries require
 * `componentDidCatch` / `static getDerivedStateFromError`, which are
 * only available on classes. The fallback UI itself is intentionally
 * minimal — no external API calls in the fallback path to avoid a
 * second failure.
 *
 * Wrap the portal family in `App.js`:
 *     <PortalErrorBoundary>
 *         <Routes>... portal routes ...</Routes>
 *     </PortalErrorBoundary>
 */
import React from "react";
import { CALENDLY_SUCCESS_TEAM_URL } from "../../config/constants";

export default class PortalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("[PortalErrorBoundary]", error, info?.componentStack);
    }

    handleRetry = () => {
        // Hard-reload rather than just clearing state — an uncaught
        // render error often leaves local state inconsistent, so a
        // fresh fetch pass is the safest recovery.
        window.location.reload();
    };

    handleHome = () => {
        window.location.assign("/");
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <section
                className="lg-section"
                data-testid="portal-error-boundary"
                style={{
                    maxWidth: 640,
                    margin: "0 auto",
                    padding: "6rem 2rem 4rem",
                }}
            >
                <div
                    className="section-label mono"
                    style={{ marginBottom: "1.5rem" }}
                >
                    Something broke
                </div>
                <h1
                    style={{
                        fontFamily: "'Fraunces', serif",
                        fontWeight: 900,
                        fontSize: "2.5rem",
                        lineHeight: 1.1,
                        letterSpacing: "-0.02em",
                        marginBottom: "1rem",
                    }}
                >
                    We hit a snag loading this page.
                </h1>
                <p
                    style={{
                        color: "var(--muted)",
                        fontSize: "1.1rem",
                        lineHeight: 1.6,
                        marginBottom: "2rem",
                    }}
                >
                    This isn&apos;t your fault. Try again, or reach the Success
                    Team and we&apos;ll sort it out.
                </p>
                <div
                    style={{
                        display: "flex",
                        gap: "1rem",
                        flexWrap: "wrap",
                    }}
                >
                    <button
                        type="button"
                        onClick={this.handleRetry}
                        data-testid="portal-error-retry"
                        style={{
                            background: "var(--accent)",
                            color: "var(--paper)",
                            padding: "0.9rem 1.6rem",
                            border: "none",
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: "0.85rem",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            cursor: "pointer",
                        }}
                    >
                        Try again →
                    </button>
                    <a
                        href={CALENDLY_SUCCESS_TEAM_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid="portal-error-success-team"
                        style={{
                            display: "inline-block",
                            background: "transparent",
                            color: "var(--ink)",
                            padding: "0.9rem 1.6rem",
                            border: "1px solid var(--ink)",
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: "0.85rem",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            textDecoration: "none",
                        }}
                    >
                        Talk to Success Team
                    </a>
                    <button
                        type="button"
                        onClick={this.handleHome}
                        data-testid="portal-error-home"
                        style={{
                            background: "transparent",
                            color: "var(--muted)",
                            padding: "0.9rem 1.6rem",
                            border: "none",
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: "0.85rem",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            cursor: "pointer",
                        }}
                    >
                        ← Back to home
                    </button>
                </div>
            </section>
        );
    }
}
